import React, {ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useState} from 'react';
import cx from 'classnames';
import {
    Input,
    Scrollbar,
    DropdownItem,
    useKeyboardNavigation,
    Text
} from '@priority-software/priority-style-react';
import {AutomationChoose} from '../../../reducer/model';
import {isRTL} from '../../../../../Localization';
import {texts} from '../../../texts';
import styles from './styles.module.scss';

type Props = {
    componentClasses?: string;
    onItemClick: (value: AutomationChoose) => void;
    data: Array<AutomationChoose>;
    autoHeightMax?: number;
    selectedValue?: number | string | boolean;
    showSearch?: boolean;
    onFocusChange?: (item: AutomationChoose, index: number) => void;
    placeholder?: string;
    noOptionsText?: string;
};

export const SearchableList = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const {
        componentClasses,
        data,
        onItemClick,
        autoHeightMax,
        selectedValue,
        showSearch = true,
        onFocusChange,
        placeholder,
        noOptionsText
    } = props;
    const [value, setValue] = useState('');

    const renderData = useMemo(() => {
        return value === ''
            ? data
            : data.filter(item => {
                  const searchValue = value.toLocaleLowerCase();
                  return (
                      item.label.toLocaleLowerCase().includes(searchValue) ||
                      item.description?.toLocaleLowerCase().includes(searchValue)
                  );
              });
    }, [value, data]);

    const handleEnter = useCallback(
        (e: KeyboardEvent, focusedIndex: number) => {
            if (e.key === 'Enter') {
                const isButtonElement = e.target instanceof HTMLButtonElement;
                // Handle selected option only if event is not triggered by button
                if (!isButtonElement) {
                    e.preventDefault();

                    const focusedOption = renderData[focusedIndex];

                    if (focusedOption) {
                        onItemClick(focusedOption);
                    }
                }
            }
        },
        [onItemClick, renderData]
    );

    const {focusedIndex, handleNavigationKeys, setFocusedIndex} = useKeyboardNavigation({
        optionsCount: renderData.length,
        defaultFocusedIndex: 0,
        handleEnter
    });

    const onSelect = useCallback(
        (item: AutomationChoose, index: number) => {
            onItemClick(item);
            setFocusedIndex(index);
        },
        [setFocusedIndex, onItemClick]
    );

    const handleValue = useCallback(
        e => {
            setValue(e.target.value);
            setFocusedIndex(0);
        },
        [setFocusedIndex]
    );

    useEffect(() => {
        if (onFocusChange) {
            onFocusChange(renderData[focusedIndex], focusedIndex);
        }
    }, [focusedIndex, renderData]);

    useEffect(() => {
        window.addEventListener('keydown', handleNavigationKeys, true);
        return () => window.removeEventListener('keydown', handleNavigationKeys, true);
    }, [handleNavigationKeys]);

    return (
        <div className={cx(styles.component, componentClasses)} ref={ref}>
            {showSearch ? (
                <Input
                    componentClasses={styles.searchInput}
                    roundedCorners
                    value={value}
                    onChange={handleValue}
                    placeholder={placeholder || texts.search_placeholder}
                    onKeyDown={e => handleEnter(e, focusedIndex)}
                />
            ) : null}

            <Scrollbar
                autoHeight
                autoHeightMax={autoHeightMax}
                rtl={isRTL()}
                shiftVerticalScrollPositionX={12}
            >
                {renderData.length === 0 ? (
                    <Text
                        componentClasses={styles.noOptions}
                        skin="paragraph-2"
                        tag="div"
                        textColor="secondary-400"
                    >
                        {noOptionsText || texts.no_options_text}
                    </Text>
                ) : (
                    <div style={{paddingRight: 1.5, paddingLeft: 1.5}}>
                        {/* these extra paddings were added to intentionally to fix border scaling issues */}
                        {renderData.map((item, index) => {
                            return (
                                <DropdownItem
                                    key={
                                        (item?.data as {id?: string})?.id ||
                                        `${item.label}-${item.value}`
                                    }
                                    onSelect={() => onSelect(item, index)}
                                    isSelected={item.value === selectedValue}
                                    isFocused={index === focusedIndex}
                                    data={item}
                                    skin="primary"
                                />
                            );
                        })}
                    </div>
                )}
            </Scrollbar>
        </div>
    );
});
